// Persistent local record of drawings the user has made — surfaced in
// the gallery's "Mine" tab. Two backing stores:
//
//   localStorage  — small JSON index of metadata (one entry per
//                   drawing). Stays in sync with the IDB store; if a
//                   blob is missing on read we filter the entry out.
//   IndexedDB     — strokes + palette + optional creative overrides,
//                   keyed by the same id. Big enough for ~30 entries
//                   without bumping localStorage's tighter quotas.
//
// Capture rules today:
//   speedrun  — saved at end-of-round (all marcs in the lineup).
//   creative  — saved on syndicate only.
// Non-syndicated creative drawings aren't saved yet; the
// session-boundary capture is a follow-up.

import { serializeStrokes } from "./strokeSerialize";

const DB_NAME = "egg-game-drawings";
const STORE = "drawings";
const DB_VERSION = 1;
const INDEX_KEY = "egg-game:v1:my-drawings";
const MAX_ENTRIES = 30;

let dbPromise = null;
function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = window.indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function idbPut(key, value) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbGet(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}

async function idbDelete(key) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function readIndex() {
  try {
    const raw = window.localStorage.getItem(INDEX_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeIndex(arr) {
  try {
    window.localStorage.setItem(INDEX_KEY, JSON.stringify(arr));
  } catch {
    // Quota exhausted or storage unavailable. Real strokes still in
    // IDB, but the index can't grow this round — acceptable
    // degradation; nothing here is load-bearing.
  }
}

function newId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// Persist a finished drawing. Returns the local id synchronously so the
// syndicate flow can attach a submission to the same entry without
// awaiting the IDB write. The IDB put runs in the background; if it
// fails the index entry orphans and listMyDrawings drops it on read.
export function recordDrawing({
  marcId,
  eggId,
  source,
  elapsedMs,
  strokes,
  palette,
  overrides,
}) {
  const id = newId();
  const blob = {
    version: 1,
    strokes: serializeStrokes(strokes),
    palette,
    ...(overrides ? { overrides } : null),
  };

  const index = readIndex();
  index.unshift({
    id,
    marcId,
    eggId,
    source,
    elapsedMs: elapsedMs ?? null,
    createdAt: new Date().toISOString(),
  });
  while (index.length > MAX_ENTRIES) {
    const dropped = index.pop();
    if (dropped) idbDelete(dropped.id).catch(() => {});
  }
  writeIndex(index);

  idbPut(id, blob).catch(() => {});
  return id;
}

export function attachSubmission(localId, submission) {
  const index = readIndex();
  const i = index.findIndex((e) => e.id === localId);
  if (i < 0) return;
  index[i] = {
    ...index[i],
    submission: {
      id: submission.id,
      initials: submission.initials,
      submittedAt: new Date().toISOString(),
    },
  };
  writeIndex(index);
}

export function listMyDrawings() {
  return readIndex();
}

export async function getDrawingBlob(localId) {
  return idbGet(localId);
}
